import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7b52bjurf8q17puvocg';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = data.get('message') as string;
    if (message && message.length > 500) return;

    setStatus('sending');
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value as string));

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-gray-900 py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Top Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
          {/* Left: Company Info */}
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/4f3ba6da-f509-4e29-a210-a9e2e628632b.png"
              alt="DUMAG DOO Logo"
              className="h-12 w-auto object-contain mb-8 brightness-0 invert"
            />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Get in Touch<br />
              <span className="text-teal-400">with DUMAG DOO</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-10 max-w-md">
              Have questions about our products or want to learn more about what we do? Reach out — we&apos;re happy to help.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                  <i className="ri-map-pin-line text-teal-400 text-lg" />
                </div>
                <div>
                  <div className="text-white/40 text-xs font-semibold tracking-widest uppercase mb-1">Address</div>
                  <div className="text-white text-sm">Bulevar Oslobodjenja 76a</div>
                  <div className="text-white text-sm">Novi Sad, 403639, Serbia</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                  <i className="ri-phone-line text-teal-400 text-lg" />
                </div>
                <div>
                  <div className="text-white/40 text-xs font-semibold tracking-widest uppercase mb-1">Phone</div>
                  <div className="text-white text-sm">+1 6065177631</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                  <i className="ri-mail-line text-teal-400 text-lg" />
                </div>
                <div>
                  <div className="text-white/40 text-xs font-semibold tracking-widest uppercase mb-1">Email</div>
                  <div className="text-white text-sm">help@dumagdoo.com</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10">
            <h3 className="text-white text-xl font-bold mb-6">Send us a message</h3>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal-500/20 mb-4">
                  <i className="ri-check-line text-teal-400 text-3xl" />
                </div>
                <p className="text-white font-semibold text-lg">Message sent!</p>
                <p className="text-gray-400 text-sm mt-2">We&apos;ll get back to you shortly.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Doe"
                      className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="How can we help?"
                    className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us more..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                  />
                  <div className="text-right text-white/30 text-xs mt-1">{charCount}/500</div>
                </div>

                {status === 'error' && (
                  <p className="text-rose-400 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3 rounded-xl transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-sm">
            &copy; {new Date().getFullYear()} DUMAG DOO. All rights reserved.
          </p>
          <p className="text-white/20 text-xs">
            Bulevar Oslobodjenja 76a, Novi Sad 403639, Serbia
          </p>
        </div>
      </div>
    </section>
  );
}
